import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Check, Shield, Star, Building2, Clock, AlertTriangle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsProvider } from '@/hooks/useIsProvider';
import { useProviderPayment } from '@/hooks/useProviderPayment';
import { useProviderProfile } from '@/hooks/useProviderProfile';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentPlatform } from '@/contexts/PaymentPlatformContext';
import { openSubscriptionManagement } from '@/services/revenueCatService';
import { NATIVE_PRICES, WEB_PRICES } from '@/services/revenueCatService';
import { Loader2 } from 'lucide-react';

export const ProviderSubscriptionCard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { data: isProvider, isLoading: isCheckingProvider } = useIsProvider();
  const { data: providerProfile, isLoading: isLoadingProfile } = useProviderProfile(user?.id);
  const { isNative, paymentMethod } = usePaymentPlatform();

  if (isCheckingProvider || isLoadingProfile) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center items-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!isProvider || !providerProfile) {
    return null;
  }

  const profile = providerProfile as any;
  const paymentStatus = profile?.provider_payment_status || 'pending_payment';
  const subscriptionStatus = profile?.subscription_status || 'pending_payment';
  const isActive = paymentStatus === 'active' && subscriptionStatus === 'active';
  const isPending = paymentStatus === 'pending' || paymentStatus === 'pending_payment' || subscriptionStatus === 'pending_payment';

  const providerPrice = isNative ? NATIVE_PRICES.provider.annual : WEB_PRICES.provider.annual;

  const getStatusBadge = () => {
    if (isActive) {
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"><Check className="w-3 h-3 mr-1" /> {t('subscription.provider.active', { defaultValue: 'Active' })}</Badge>;
    }
    if (isPending) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"><Clock className="w-3 h-3 mr-1" /> {t('subscription.provider.pending', { defaultValue: 'Pending' })}</Badge>;
    }
    return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" /> {t('subscription.provider.inactive', { defaultValue: 'Inactive' })}</Badge>;
  };

  const benefits = [
    t('subscription.provider.benefit.listing', { defaultValue: 'Your business listed in the provider directory' }),
    t('subscription.provider.benefit.reviews', { defaultValue: 'Customer reviews & ratings system' }),
    t('subscription.provider.benefit.analytics', { defaultValue: 'Business analytics dashboard' }),
    t('subscription.provider.benefit.support', { defaultValue: 'Priority customer support' }),
    t('subscription.provider.benefit.promotions', { defaultValue: 'Geo-targeted promotions' }),
    t('subscription.provider.benefit.storage', { defaultValue: '3 GB provider photo storage (separate from personal storage)' }),
  ];

  const handleManageSubscription = async () => {
    if (isNative) {
      try {
        await openSubscriptionManagement();
      } catch (error) {
        console.error('Error opening subscription management:', error);
      }
    }
  };

  const providerTypeName = profile?.provider_type
    ? t(`services.providerTypes.${profile.provider_type}`, { defaultValue: profile.provider_type.replace(/_/g, ' ') })
    : '';

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-full">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              {t('subscription.provider.title', { defaultValue: 'Service Provider Subscription' })}
              {getStatusBadge()}
            </CardTitle>
            <CardDescription>
              {profile?.business_name} • {providerTypeName}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pricing info */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div>
            <p className="font-semibold text-lg">${providerPrice.toFixed(2)} USD / {t('common.year', { defaultValue: 'year' })}</p>
            <p className="text-xs text-muted-foreground">
              {t('subscription.provider.billedAnnually', { defaultValue: 'Billed annually' })}
              {isNative && (
                <span className="ml-1">
                  • {paymentMethod === 'apple' ? '🍎 Apple' : '🤖 Google Play'}
                </span>
              )}
            </p>
          </div>
          {profile?.verified && (
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              <Shield className="w-3 h-3 mr-1" /> {t('subscription.provider.verified', { defaultValue: 'Verified' })}
            </Badge>
          )}
        </div>

        {/* Benefits */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            {t('subscription.provider.includedBenefits', { defaultValue: 'Included Benefits' })}
          </h4>
          <ul className="space-y-1.5">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Note about dual subscription */}
        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-blue-800 dark:text-blue-200">
            <strong>{t('subscription.provider.dualNote.title', { defaultValue: '💡 Did you know?' })}</strong>{' '}
            {t('subscription.provider.dualNote.description', { defaultValue: 'Your provider subscription is separate from your personal pet owner plan below. Provider photo uploads use your 3 GB provider storage allocation.' })}
          </p>
        </div>

        {/* Manage button for native */}
        {isNative && isActive && (
          <Button variant="outline" onClick={handleManageSubscription} className="w-full">
            <ExternalLink className="w-4 h-4 mr-2" />
            {t('subscription.manageSubscription', { defaultValue: 'Manage Subscription' })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
